import { Link, useNavigate } from "react-router-dom"
import { Search, ChevronDown, Menu, X } from "lucide-react"
import { useState } from "react"
import { redirectToSso } from "@alascorporation/api-client"
import { useAuthStore } from "../../stores/authStore"
import { programs } from "../../config/schools"
import { ssoConfig } from "../../lib/sso"

const navPrograms = programs

export default function PublicNavbar() {
  const { isAuthenticated, logout } = useAuthStore()
  const navigate = useNavigate()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")

  const handleLogout = () => {
    logout()
    navigate("/")
  }

  return (
    <header className="sticky top-0 z-50 glass border-x-0 border-t-0">
      <div className="section-shell">
        <div className="flex h-14 items-center gap-4">
          <Link to="/" className="mr-2 flex items-center gap-2 shrink-0">
            <img 
              src="https://satelab.org/Aviaci%C3%B3n_And_Space_Academy.webp" 
              alt="SateLab Academy" 
              className="h-10 w-auto object-contain"
            />
            <span className="text-lg font-bold tracking-tight text-foreground hidden sm:inline-block">SateLab Academy</span>
          </Link>

          <div className="group relative hidden md:block">
            <button className="flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
              Programas
              <ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:rotate-180" />
            </button>
            <div className="invisible absolute left-0 top-full mt-1 w-64 rounded-lg border border-border bg-card p-1.5 opacity-0 shadow-xl transition-[opacity,visibility] group-hover:visible group-hover:opacity-100">
              {navPrograms.map((program) => (
                <Link
                  key={program.label}
                  to={program.href}
                  className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  <program.icon className="h-4 w-4 text-primary" />
                  {program.label}
                </Link>
              ))}
            </div>
          </div>

          <nav className="hidden items-center gap-1 md:flex">
            <Link to="/courses" className="rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
              Cursos
            </Link>
          </nav>

          <div className="relative ml-auto hidden flex-1 max-w-xs md:flex lg:max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Buscar cursos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && searchQuery.trim()) {
                  navigate(`/courses?search=${encodeURIComponent(searchQuery.trim())}`)
                  setSearchQuery("")
                }
              }}
              className="h-9 w-full rounded-md border border-border bg-muted pl-9 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          <div className="ml-2 hidden items-center gap-2 md:flex">
            {isAuthenticated ? (
              <div className="flex items-center gap-2">
                <Link
                  to="/dashboard"
                  className="rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  Mi perfil
                </Link>
                <button
                  onClick={handleLogout}
                  className="rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
                >
                  Salir
                </button>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => redirectToSso(ssoConfig, "/dashboard")}
                  className="rounded-md px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
                >
                  Ingresar
                </button>
                <button
                  type="button"
                  onClick={() => redirectToSso(ssoConfig, "/dashboard")}
                  className="btn-platzi"
                >
                  Empieza ahora
                </button>
              </>
            )}
          </div>

          <button
            className="ml-auto rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-foreground md:hidden"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-border bg-popover md:hidden">
          <div className="section-shell py-4 space-y-1">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Buscar cursos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && searchQuery.trim()) {
                    navigate(`/courses?search=${encodeURIComponent(searchQuery.trim())}`)
                    setSearchQuery("")
                    setMobileOpen(false)
                  }
                }}
                className="h-10 w-full rounded-md border border-border bg-muted pl-9 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
              />
            </div>
            <Link to="/courses" onClick={() => setMobileOpen(false)} className="block rounded-md px-3 py-2.5 text-sm font-medium text-foreground hover:bg-accent">Cursos</Link>
            <div className="my-2 border-t border-border" />
            {navPrograms.map((program) => (
              <Link key={program.label} to={program.href} onClick={() => setMobileOpen(false)} className="flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-muted-foreground hover:bg-accent hover:text-foreground">
                <program.icon className="h-4 w-4 text-primary" />
                {program.label}
              </Link>
            ))}
            <div className="my-2 border-t border-border" />
            {isAuthenticated ? (
              <>
                <Link to="/dashboard" onClick={() => setMobileOpen(false)} className="block rounded-md px-3 py-2.5 text-sm font-medium text-foreground hover:bg-accent">Mi perfil</Link>
                <button onClick={() => { handleLogout(); setMobileOpen(false) }} className="block w-full rounded-md px-3 py-2.5 text-left text-sm font-medium text-muted-foreground hover:bg-accent">Salir</button>
              </>
            ) : (
              <>
                <button type="button" onClick={() => { setMobileOpen(false); redirectToSso(ssoConfig, "/dashboard") }} className="block w-full rounded-md px-3 py-2.5 text-left text-sm font-medium text-foreground hover:bg-accent">Ingresar</button>
                <button type="button" onClick={() => { setMobileOpen(false); redirectToSso(ssoConfig, "/dashboard") }} className="btn-platzi w-full justify-center mt-2">Empieza ahora</button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  )
}
